import { KEY_CODES } from "../../../gearshiftGame/constants";

/**
 * Separated phases for mission two tutorials.
 * Mission two includes truck phases (1.1 and 1.2) specific to this mission.
 */
export function buildPhase0(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      trial_duration: newGame.targetLaunchDelay,
      on_load: () => newGame.startPhase0(),
      on_finish: (data) => {
        data.name = "initializeTarget";
        data.driverSpeed = newGame.driverCarSpeed;
        data.distanceToTarget = newGame.driverDistanceToTarget;
        data.trialStart = {
          name: "trialStart",
          driverSpeed: newGame.driverCarSpeed,
          timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
        };
      },
    },
  ];
}

export function buildPhase1(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      on_load: () => newGame.startPhase1(),
      on_finish: (data) => {
        data.name = "engageTarget";
        data.cueCarOn = {
          name: "cueCarOn",
          driverSpeed: newGame.driverCarSpeed,
          timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
          distanceToTarget: newGame.driverDistanceToTarget,
          csi: newGame.csi,
        };
        if (data.crashed) jsPsych.endCurrentTimeline();
        else data.crashed = false;
      },
    },
  ];
}

/**
 * Phase 1.1: Truck signal is displayed and waits for user to make a decision
 * Phase ends when time runs out or user selects a lane
 * MISSION TWO SPECIFIC: Truck can appear and block the target car
 */
export function buildPhase1_1(newGame, jsPsych) {
  return [
    {
      choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon", "Space"],
      on_load: () => {
        const game = window._gear_currentGame || newGame;
        if (game) game.startPhase1_1();
      },
      on_finish: (data) => {
        const game = window._gear_currentGame || newGame;
        data.name = "Phase1A_truckBlockingTarget";
        if ((data.response_event_code ?? data.response) !== "Space") {
          if (game) game.playEffect("shiftLane");
          if (game) game.followTruckSignal = 1;
          data.keyCode =
            KEY_CODES[
            jsPsych.data.get().last(1).values()[0].response_event_code
            ?? jsPsych.data.get().last(1).values()[0].response
            ];
          data.driverMovedTruck = 2; // driver did not try to move truck
        } else {
          if (game) game.followTruckSignal = 0;
          data.keyCode = 0; // spacebar presses recorded as zeros
          if (game && game.truckMoves === 0) {
            data.driverMovedTruck = 0;
            if (game) game.playEffect("lockOn");
          } else {
            data.driverMovedTruck = 1;
          }
        }
        if (game) data.followTruckSignal = game.followTruckSignal;
      },
    },
  ];
}

/**
 * Phase 1.2: Handles truck movement off the screen
 * MISSION TWO SPECIFIC: Truck can move right and off highway, or remain in place
 * Conditional phase - only runs when user did not choose a lane in phase 1.1
 */
export function buildPhase1_2(newGame) {
  return [
    {
      choices: () => {
        const game = window._gear_currentGame || newGame;
        return game && game.truckMoves === 1
          ? "NO_KEYS"
          : ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"];
      },
      on_load: () => {
        const game = window._gear_currentGame || newGame;
        if (game) game.startPhase1_2();
      },
      on_finish: (data) => {
        data.name = "Phase1B_truckBlockingTarget";
        if (data.response !== undefined) {
          const key = data.response_event_code ?? data.response;
          data.keyCode = KEY_CODES[key];
        }
        if ((data.response_event_code ?? data.response) === "Space") {
          data.keyCode = 0;
        }
      },
    },
  ];
}

export function buildPhase2(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      trial_duration: newGame.csi,
      on_load: () => newGame.startPhase2(),
      on_finish: (data) => {
        data.name = "licensePlateOn";
        data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
        data.driverSpeed = newGame.driverCarSpeed;
        data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
      },
    },
  ];
}

export function buildPhase3(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      on_load: () => newGame.startPhase3(),
      on_finish: (data) => {
        data.name = "responseKeyPressed";
        data.response_keypress_time = data.time_elapsed;
        data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null;
        data.responseTime = data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
        data.driverSpeed = newGame.driverCarSpeed;
        // displayLicense()/captureLicensePlateOnTime() only run once startPhase3's on_load
        // fires, which is after the "licensePlateOn" (phase 2) row already saved - so this
        // is the earliest row where the value is actually available
        data.license_plate_on_time = newGame.licensePlateOnTime;
      },
    },
  ];
}

export function buildPhase4(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      on_load: () => newGame.startPhase4(jsPsych.data.get().last(1).values()[0].keyCode),
      on_finish: (data) => {
        data.name = "driverInResponseLane";
        data.driverSpeed = newGame.driverCarSpeed;
      },
    },
  ];
}

export function buildPhase5(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      on_load: () => newGame.startPhase5(),
      on_finish: (data) => {
        data.name = "packageDisappears";
        data.driverSpeed = newGame.driverCarSpeed;
        data.emotion = newGame.emotion;
        data.packageInTargetLane = {
          timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          driverSpeed: newGame.driverCarSpeed,
          packageDistance: newGame.packageDistanceToTarget,
        };
        data.targetDisappears = {
          timestamp: newGame.targetCarDisappearTimestamp,
          driverSpeed: newGame.driverCarSpeed,
        };
      },
    },
  ];
}

export function buildPhase7(newGame, jsPsych) {
  return [
    {
      choices: "NO_KEYS",
      on_load: () => newGame.startPhase7(),
      on_finish: (data) => {
        data.name = "driverAtCenter";
        data.driverToCenter = {
          timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          driverSpeed: newGame.driverCarSpeed,
        };
        data.driverSpeed = newGame.driverCarSpeed;
      },
    },
  ];
}
